import { formatBytes } from "@/shared/bytes";

type Props = {
  keepPdfByDefault: boolean;
  documentCount: number;
  annotationCount: number;
  storage: { usage: number; quota: number; persisted: boolean | null } | null;
  busy: boolean;
  onChangeKeepPdf(value: boolean): void;
  onExportLibrary(): void;
  onClearAll(): void;
  onBack(): void;
};

export function SettingsScreen({
  keepPdfByDefault,
  documentCount,
  annotationCount,
  storage,
  busy,
  onChangeKeepPdf,
  onExportLibrary,
  onClearAll,
  onBack,
}: Props) {
  return (
    <main className="settings-shell app-screen">
      <header className="settings-header">
        <button className="text-button" type="button" onClick={onBack}>
          ← Library
        </button>
        <a className="wordmark" href="/">
          Marginalia
        </a>
        <span />
      </header>

      <section className="settings-intro">
        <p className="eyebrow">Storage & privacy</p>
        <h1>Your browser is the vault.</h1>
        <p>
          Marginalia has no account, application server, or cloud database.
          Clearing this site’s browser data can erase your local library, so
          export notes you care about.
        </p>
      </section>

      <section className="settings-grid">
        <article className="settings-card">
          <p className="eyebrow">Local data</p>
          <h2>{documentCount} documents</h2>
          <p>
            {annotationCount} saved{" "}
            {annotationCount === 1 ? "annotation" : "annotations"}
          </p>
          {storage ? (
            <div className="storage-meter">
              <span
                style={{
                  width: `${Math.min(
                    100,
                    (storage.usage / Math.max(storage.quota, 1)) * 100,
                  )}%`,
                }}
              />
            </div>
          ) : null}
          <small>
            {storage
              ? `${formatBytes(storage.usage)} used of approximately ${formatBytes(
                  storage.quota,
                )}. ${
                  storage.persisted
                    ? "Persistent storage was granted."
                    : "The browser may reclaim this data."
                }`
              : "Storage estimates are not available in this browser."}
          </small>
        </article>

        <article className="settings-card">
          <p className="eyebrow">PDF retention</p>
          <label className="settings-toggle">
            <span>
              <strong>Keep new PDFs by default</strong>
              <small>
                Store a local browser copy so documents reopen without a file
                picker and remain available offline.
              </small>
            </span>
            <input
              type="checkbox"
              checked={keepPdfByDefault}
              onChange={(event) => onChangeKeepPdf(event.target.checked)}
            />
          </label>
        </article>

        <article className="settings-card full">
          <p className="eyebrow">Ownership</p>
          <div className="settings-action-row">
            <div>
              <h2>Back up the library</h2>
              <p>
                Download document identities, reading positions, notes, and
                highlights as versioned JSON. PDF files are not included.
              </p>
            </div>
            <button
              className="secondary-button"
              type="button"
              disabled={busy || !documentCount}
              onClick={onExportLibrary}
            >
              Export library
            </button>
          </div>
        </article>

        <article className="settings-card full danger-zone">
          <p className="eyebrow">Danger zone</p>
          <div className="settings-action-row">
            <div>
              <h2>Remove all local data</h2>
              <p>
                Delete every PDF copy, note, highlight, reading position, and
                setting stored by this origin.
              </p>
            </div>
            <button
              className="danger-button"
              type="button"
              disabled={busy || !documentCount}
              onClick={onClearAll}
            >
              Delete local library
            </button>
          </div>
        </article>
      </section>
    </main>
  );
}
